/**
 * ScoreManager - Handles all score/collectible tracking
 * Decoupled from game logic through EventBus
 */

import eventBus, { GameEvents } from '../EventBus';

export default class ScoreManager {
    constructor() {
        this.currentScore = 0;
        this.totalStars = 0;
        this.levelScores = new Map();
        this.bestTimes = new Map();
        
        this.setupEventListeners();
    }

    setupEventListeners() {
        eventBus.on(GameEvents.STAR_COLLECTED, this.onStarCollected, this);
        eventBus.on(GameEvents.LEVEL_START, this.onLevelStart, this);
        eventBus.on(GameEvents.LEVEL_COMPLETE, this.onLevelComplete, this);
        eventBus.on(GameEvents.LEVEL_RESTART, this.onLevelRestart, this);
    }

    onLevelStart(data) {
        this.currentScore = 0;
        this.totalStars = data.totalStars || 0;
    }

    onStarCollected(data) {
        this.currentScore++;
        
        // Check if all stars collected
        if (this.currentScore >= this.totalStars) {
            eventBus.emit(GameEvents.ALL_STARS_COLLECTED, {
                level: data.level,
                score: this.currentScore
            });
        }
    }

    onLevelComplete(data) {
        const { level, time } = data;
        
        // Store level score
        const previousScore = this.levelScores.get(level) || 0;
        if (this.currentScore > previousScore) {
            this.levelScores.set(level, this.currentScore);
        }
        
        // Store best time
        const previousBest = this.bestTimes.get(level) || Infinity;
        if (time < previousBest) {
            this.bestTimes.set(level, time);
        }
    }

    onLevelRestart() {
        this.currentScore = 0;
    }

    getScore() {
        return this.currentScore;
    }

    getTotalStars() {
        return this.totalStars;
    }

    getLevelScore(level) {
        return this.levelScores.get(level) || 0;
    }

    getBestTime(level) {
        return this.bestTimes.get(level) || null;
    }

    getTotalScore() {
        let total = 0;
        this.levelScores.forEach(score => total += score);
        return total;
    }

    // Serialize for saving
    serialize() {
        return {
            levelScores: Object.fromEntries(this.levelScores),
            bestTimes: Object.fromEntries(this.bestTimes)
        };
    }

    // Deserialize from saved data
    deserialize(data) {
        if (data.levelScores) {
            this.levelScores = new Map(Object.entries(data.levelScores));
        }
        if (data.bestTimes) {
            this.bestTimes = new Map(Object.entries(data.bestTimes));
        }
    }

    destroy() {
        eventBus.off(GameEvents.STAR_COLLECTED, this.onStarCollected, this);
        eventBus.off(GameEvents.LEVEL_START, this.onLevelStart, this);
        eventBus.off(GameEvents.LEVEL_COMPLETE, this.onLevelComplete, this);
        eventBus.off(GameEvents.LEVEL_RESTART, this.onLevelRestart, this);
    }
}
